import {
  OBJECT_STORAGE_CERTIFICATE_CONTENT_TYPES,
  PresignUploadBodySchema,
  PresignUploadResponseSchema,
  OBJECT_STORAGE_IMAGE_CONTENT_TYPES,
  OBJECT_STORAGE_VIDEO_CONTENT_TYPES,
  type PresignUploadBody,
} from '@zrsystem/contracts';

import { getHttpClient } from '@/shared/api/http-client.js';
import { HttpError } from '@/shared/api/types.js';

const STORAGE_PRESIGN_PATH = '/api/storage/presign-upload';

/** Mirrors API default `OBJECT_STORAGE_MAX_UPLOAD_BYTES` for client-side precheck. */
export const OBJECT_STORAGE_DEFAULT_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export function isAllowedInspectionImageType(
  contentType: string,
): contentType is PresignUploadBody['contentType'] {
  return (OBJECT_STORAGE_IMAGE_CONTENT_TYPES as readonly string[]).includes(contentType);
}

export function isAllowedBrandLogoImageType(
  contentType: string,
): contentType is PresignUploadBody['contentType'] {
  return isAllowedInspectionImageType(contentType);
}

export function isAllowedInspectionVideoType(
  contentType: string,
): contentType is PresignUploadBody['contentType'] {
  return (OBJECT_STORAGE_VIDEO_CONTENT_TYPES as readonly string[]).includes(contentType);
}

export function isAllowedInspectionMediaType(contentType: string): boolean {
  return isAllowedInspectionImageType(contentType) || isAllowedInspectionVideoType(contentType);
}

function certificateContentTypeForFile(
  file: File,
): (typeof OBJECT_STORAGE_CERTIFICATE_CONTENT_TYPES)[number] {
  if ((OBJECT_STORAGE_CERTIFICATE_CONTENT_TYPES as readonly string[]).includes(file.type)) {
    return file.type as (typeof OBJECT_STORAGE_CERTIFICATE_CONTENT_TYPES)[number];
  }
  return 'application/octet-stream';
}

export function isAllowedFiscalCertificateFile(file: File): boolean {
  const lowerName = file.name.toLowerCase();
  return (
    lowerName.endsWith('.pfx') ||
    lowerName.endsWith('.p12') ||
    (OBJECT_STORAGE_CERTIFICATE_CONTENT_TYPES as readonly string[]).includes(file.type)
  );
}

export async function presignInspectionPhotoUpload(file: File) {
  if (!isAllowedInspectionImageType(file.type)) {
    throw new HttpError({
      status: 422,
      code: 'OBJECT_STORAGE_INVALID_CONTENT_TYPE',
      message: 'Invalid content type',
    });
  }

  const body = PresignUploadBodySchema.parse({
    purpose: 'inspection-photo',
    contentType: file.type,
    contentLength: file.size,
  });

  const raw = await getHttpClient().post<unknown>(STORAGE_PRESIGN_PATH, body);
  return PresignUploadResponseSchema.parse(raw);
}

export async function presignFiscalCertificateUpload(file: File) {
  if (!isAllowedFiscalCertificateFile(file)) {
    throw new HttpError({
      status: 422,
      code: 'OBJECT_STORAGE_INVALID_CONTENT_TYPE',
      message: 'Invalid certificate file type',
    });
  }

  const body = PresignUploadBodySchema.parse({
    purpose: 'fiscal-place-certificate',
    contentType: certificateContentTypeForFile(file),
    contentLength: file.size,
  });

  const raw = await getHttpClient().post<unknown>(STORAGE_PRESIGN_PATH, body);
  return PresignUploadResponseSchema.parse(raw);
}

export async function uploadFileToPresignedUrl(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });

  if (!response.ok) {
    throw new HttpError({
      status: 502,
      code: 'OBJECT_STORAGE_UPLOAD_FAILED',
      message: 'Upload failed',
    });
  }
}

export async function uploadInspectionPhoto(
  file: File,
): Promise<{ storageKey: string; objectUrl: string }> {
  const presign = await presignInspectionPhotoUpload(file);
  await uploadFileToPresignedUrl(presign.uploadUrl, file);
  const objectUrl = presign.uploadUrl.split('?')[0] ?? presign.uploadUrl;
  return { storageKey: presign.storageKey, objectUrl };
}

export async function presignInspectionMediaUpload(file: File) {
  const isImage = isAllowedInspectionImageType(file.type);
  const isVideo = isAllowedInspectionVideoType(file.type);
  if (!isImage && !isVideo) {
    throw new HttpError({
      status: 422,
      code: 'OBJECT_STORAGE_INVALID_CONTENT_TYPE',
      message: 'Invalid content type',
    });
  }

  const body = PresignUploadBodySchema.parse({
    purpose: isVideo ? 'inspection-video' : 'inspection-photo',
    contentType: file.type,
    contentLength: file.size,
  });

  const raw = await getHttpClient().post<unknown>(STORAGE_PRESIGN_PATH, body);
  return PresignUploadResponseSchema.parse(raw);
}

export async function uploadInspectionMedia(
  file: File,
): Promise<{ storageKey: string; objectUrl: string; mediaType: 'IMAGE' | 'VIDEO' }> {
  const mediaType: 'IMAGE' | 'VIDEO' = isAllowedInspectionVideoType(file.type) ? 'VIDEO' : 'IMAGE';
  const presign = await presignInspectionMediaUpload(file);
  await uploadFileToPresignedUrl(presign.uploadUrl, file);
  const objectUrl = presign.uploadUrl.split('?')[0] ?? presign.uploadUrl;
  return { storageKey: presign.storageKey, objectUrl, mediaType };
}

export async function presignReceptionSignatureUpload(file: File) {
  if (!isAllowedInspectionImageType(file.type)) {
    throw new HttpError({
      status: 422,
      code: 'OBJECT_STORAGE_INVALID_CONTENT_TYPE',
      message: 'Invalid content type',
    });
  }

  const body = PresignUploadBodySchema.parse({
    purpose: 'reception-signature',
    contentType: file.type,
    contentLength: file.size,
  });

  const raw = await getHttpClient().post<unknown>(STORAGE_PRESIGN_PATH, body);
  return PresignUploadResponseSchema.parse(raw);
}

export async function uploadReceptionSignature(
  file: File,
): Promise<{ storageKey: string; objectUrl: string }> {
  const presign = await presignReceptionSignatureUpload(file);
  await uploadFileToPresignedUrl(presign.uploadUrl, file);
  const objectUrl = presign.uploadUrl.split('?')[0] ?? presign.uploadUrl;
  return { storageKey: presign.storageKey, objectUrl };
}

export async function uploadFiscalCertificate(file: File): Promise<{ storageKey: string }> {
  const presign = await presignFiscalCertificateUpload(file);
  await uploadFileToPresignedUrl(presign.uploadUrl, file);
  return { storageKey: presign.storageKey };
}

export async function presignBrandLogoUpload(file: File) {
  if (!isAllowedBrandLogoImageType(file.type)) {
    throw new HttpError({
      status: 422,
      code: 'OBJECT_STORAGE_INVALID_CONTENT_TYPE',
      message: 'Invalid content type',
    });
  }
  if (file.size > OBJECT_STORAGE_DEFAULT_MAX_UPLOAD_BYTES) {
    throw new HttpError({
      status: 422,
      code: 'OBJECT_STORAGE_FILE_TOO_LARGE',
      message: 'File too large',
    });
  }

  const body = PresignUploadBodySchema.parse({
    purpose: 'catalog-brand-logo',
    contentType: file.type,
    contentLength: file.size,
  });

  const raw = await getHttpClient().post<unknown>(STORAGE_PRESIGN_PATH, body);
  return PresignUploadResponseSchema.parse(raw);
}

export async function uploadBrandLogo(
  file: File,
): Promise<{ storageKey: string; objectUrl: string }> {
  const presign = await presignBrandLogoUpload(file);
  await uploadFileToPresignedUrl(presign.uploadUrl, file);
  const objectUrl = presign.uploadUrl.split('?')[0] ?? presign.uploadUrl;
  return { storageKey: presign.storageKey, objectUrl };
}

export async function presignCatalogProductImageUpload(file: File) {
  if (!isAllowedBrandLogoImageType(file.type)) {
    throw new HttpError({
      status: 422,
      code: 'OBJECT_STORAGE_INVALID_CONTENT_TYPE',
      message: 'Invalid content type',
    });
  }
  if (file.size > OBJECT_STORAGE_DEFAULT_MAX_UPLOAD_BYTES) {
    throw new HttpError({
      status: 422,
      code: 'OBJECT_STORAGE_FILE_TOO_LARGE',
      message: 'File too large',
    });
  }

  const body = PresignUploadBodySchema.parse({
    purpose: 'catalog-product-image',
    contentType: file.type,
    contentLength: file.size,
  });

  const raw = await getHttpClient().post<unknown>(STORAGE_PRESIGN_PATH, body);
  return PresignUploadResponseSchema.parse(raw);
}

export async function uploadCatalogProductImage(
  file: File,
): Promise<{ storageKey: string; objectUrl: string }> {
  const presign = await presignCatalogProductImageUpload(file);
  await uploadFileToPresignedUrl(presign.uploadUrl, file);
  const objectUrl = presign.uploadUrl.split('?')[0] ?? presign.uploadUrl;
  return { storageKey: presign.storageKey, objectUrl };
}

export function isAllowedCampaignImageType(
  contentType: string,
): contentType is PresignUploadBody['contentType'] {
  return isAllowedInspectionImageType(contentType);
}

export async function presignCampaignImageUpload(file: File) {
  if (!isAllowedCampaignImageType(file.type)) {
    throw new HttpError({
      status: 422,
      code: 'OBJECT_STORAGE_INVALID_CONTENT_TYPE',
      message: 'Invalid content type',
    });
  }
  if (file.size > OBJECT_STORAGE_DEFAULT_MAX_UPLOAD_BYTES) {
    throw new HttpError({
      status: 422,
      code: 'OBJECT_STORAGE_FILE_TOO_LARGE',
      message: 'File too large',
    });
  }

  const body = PresignUploadBodySchema.parse({
    purpose: 'notification-campaign-image',
    contentType: file.type,
    contentLength: file.size,
  });

  const raw = await getHttpClient().post<unknown>(STORAGE_PRESIGN_PATH, body);
  return PresignUploadResponseSchema.parse(raw);
}

export async function uploadCampaignImage(
  file: File,
): Promise<{ storageKey: string; objectUrl: string }> {
  const presign = await presignCampaignImageUpload(file);
  await uploadFileToPresignedUrl(presign.uploadUrl, file);
  const objectUrl = presign.uploadUrl.split('?')[0] ?? presign.uploadUrl;
  return { storageKey: presign.storageKey, objectUrl };
}
